<template>
  <vx-card title="Tiempo promedio de finalización por tipo de proceso" id="card">
    <!-- LOADING -->
    <div
      v-if="uiState === 'loading'"
      class="gw-empty-state"
      style="height:350px;"
    >
      <div class="gw-spinner" aria-label="Cargando"></div>
      <div class="gw-empty-title">Cargando información…</div>
      <div class="gw-empty-subtitle">Un momento por favor</div>
    </div>

    <!-- READY (CHART) -->
    <apexcharts
      v-else-if="uiState === 'ready' && renderGraphic"
      type="bar"
      height="350"
      :options="chartOptions"
      :series="series"
    />

    <!-- EMPTY / ERROR -->
    <div
      v-else
      class="gw-empty-state"
      style="height:350px;"
    >
      <img
        class="gw-empty-img"
        :src="emptyStateImage"
        :alt="emptyStateAlt"
      />
      <div class="gw-empty-title">{{ emptyStateTitle }}</div>
      <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
    </div>
  </vx-card>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import api from "../../mixins/api";
import { mapGetters } from "vuex";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,
      requestKey: null,
      consultHandler: null,

      // S3 illustrations
      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      series: [
        {
          name: "Promedio (días)",
          data: [],
        },
      ],

      chartOptions: {
        chart: {
          height: 350,
          type: "bar",
        },
        plotOptions: {
          bar: {
            columnWidth: "50%",
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val) {
            var n = typeof val === "string" ? parseFloat(val) : val;
            if (typeof n === "number" && !isNaN(n)) return n.toFixed(2);
            return "";
          },
        },
        stroke: {
          width: 2,
        },
        colors: ["#5F0091"],
        xaxis: {
          categories: [],
          labels: {
            rotate: -45,
            trim: true,
          },
        },
        yaxis: {
          title: {
            text: "Promedio (días)",
          },
          labels: {
            formatter: function (value) {
              var n = typeof value === "string" ? parseFloat(value) : value;
              if (typeof n === "number" && !isNaN(n)) return n.toFixed(2);
              return value;
            },
          },
        },
        tooltip: {
          y: {
            formatter: function (val, opts) {
              var dataPointIndex = opts && opts.dataPointIndex;
              var w = opts && opts.w;
              var hours =
                w &&
                w.config &&
                w.config.__avgHoursMeta &&
                w.config.__avgHoursMeta[dataPointIndex];

              var days = typeof val === "string" ? parseFloat(val) : val;
              var daysText =
                typeof days === "number" && !isNaN(days)
                  ? days.toFixed(2) + " días"
                  : val + " días";

              if (hours != null) {
                var h = typeof hours === "string" ? parseFloat(hours) : hours;
                if (typeof h === "number" && !isNaN(h)) {
                  return daysText + " (" + h.toFixed(2) + " horas)";
                }
              }
              return daysText;
            },
          },
        },
        // metadata auxiliar para tooltip
        __avgHoursMeta: [],
      },
    };
  },

  components: {
    apexcharts: VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },
    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },
    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },
    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/avg-completion-time-by-type?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        url =
          this.url() +
          path +
          "&start_date=" +
          this.startDate +
          "&end_date=" +
          this.endDate;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) url += "&city_id=" + this.cityId;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [{ name: "Promedio (días)", data: [] }];
      this.chartOptions.xaxis = { categories: [] };
      this.chartOptions.__avgHoursMeta = [];
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    async setReady(categories, daysSeries, hoursMeta) {
      // remount
      this.renderGraphic = false;
      await this.$nextTick();

      this.chartOptions.xaxis = { categories: categories };
      this.chartOptions.__avgHoursMeta = hoursMeta;

      this.series = [
        {
          name: "Promedio (días)",
          data: daysSeries,
        },
      ];

      // si todos 0 => empty
      var hasValue = false;
      for (var i = 0; i < daysSeries.length; i++) {
        if (Number(daysSeries[i]) > 0) {
          hasValue = true;
          break;
        }
      }

      if (!hasValue) {
        this.setEmpty();
        return;
      }

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[avg-completion-time-by-type] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // race condition guard
        if (this.requestKey !== key) {
          console.log("[avg-completion-time-by-type] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[avg-completion-time-by-type] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        var data = request.data;

        if (!Array.isArray(data) || data.length === 0) {
          this.setEmpty();
          return;
        }

        var categories = [];
        var daysSeries = [];
        var hoursMeta = [];

        for (var i = 0; i < data.length; i++) {
          var row = data[i];
          if (!row) continue;

          categories.push(this.$t(row.name));

          var d = row.avg_days;
          var h = row.avg_hours;

          if (typeof d === "string") d = parseFloat(d);
          if (typeof h === "string") h = parseFloat(h);

          d = typeof d === "number" && !isNaN(d) ? d : 0;
          h = typeof h === "number" && !isNaN(h) ? h : null;

          daysSeries.push(d);
          hoursMeta.push(h);
        }

        if (categories.length === 0) {
          this.setEmpty();
          return;
        }

        await this.setReady(categories, daysSeries, hoursMeta);

        console.log("[avg-completion-time-by-type] categories:", categories);
        console.log("[avg-completion-time-by-type] series days:", daysSeries);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[avg-completion-time-by-type] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () =>
      this.initializeData("eventBus:consultData");
    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
